/**
 * Soundboard Local Storage Utilities
 * Handles persistence of soundboard items and settings
 */

export interface SoundboardItem {
  id: string;
  name: string;
  file: File;
  audioUrl: string;
  order: number;
  createdAt: Date;
  fileSize: number;
  duration?: number; // Audio duration in seconds
}

export interface SoundboardStorage {
  items: SoundboardItem[];
  isExpanded: boolean;
  lastUpdated: Date;
}

const STORAGE_KEY = 'aipx-soundboard';
const MAX_STORAGE_SIZE = 50 * 1024 * 1024; // 50MB limit
const MAX_ITEMS = 20; // Maximum number of soundboard items

class SoundboardStorageManager {
  private static instance: SoundboardStorageManager;

  public static getInstance(): SoundboardStorageManager {
    if (!SoundboardStorageManager.instance) {
      SoundboardStorageManager.instance = new SoundboardStorageManager();
    }
    return SoundboardStorageManager.instance;
  }

  /**
   * Load soundboard data from localStorage
   */
  public loadSoundboard(): SoundboardStorage {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        return this.getDefaultStorage();
      }

      const parsed = JSON.parse(stored);
      
      // Convert stored items back to proper format
      const items: SoundboardItem[] = parsed.items
        .filter((item: any) => item && item.fileData && item.name && item.mimeType) // Filter out invalid items
        .map((item: any) => {
          try {
            const file = this.fileFromBase64(item.fileData, item.name, item.mimeType);
            return {
              ...item,
              file,
              createdAt: new Date(item.createdAt),
              audioUrl: item.audioUrl || URL.createObjectURL(file)
            };
          } catch (error) {
            console.error('Error processing soundboard item:', error, item);
            return null;
          }
        })
        .filter((item: SoundboardItem | null): item is SoundboardItem => item !== null); // Remove failed items

      return {
        items,
        isExpanded: parsed.isExpanded ?? false,
        lastUpdated: new Date(parsed.lastUpdated || Date.now())
      };
    } catch (error) {
      console.error('Error loading soundboard from localStorage:', error);
      return this.getDefaultStorage();
    }
  }

  /**
   * Save soundboard data to localStorage
   */
  public async saveSoundboard(storage: SoundboardStorage): Promise<void> {
    try {
      // Convert items to storage format
      const itemsToStore = await Promise.all(storage.items.map(async item => ({
        id: item.id,
        name: item.name,
        fileData: await this.fileToBase64(item.file),
        mimeType: item.file.type,
        audioUrl: item.audioUrl,
        order: item.order,
        createdAt: item.createdAt.toISOString(),
        fileSize: item.fileSize,
        duration: item.duration
      })));

      const storageData = {
        items: itemsToStore,
        isExpanded: storage.isExpanded,
        lastUpdated: new Date().toISOString()
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(storageData));
    } catch (error) {
      console.error('Error saving soundboard to localStorage:', error);
    }
  }

  /**
   * Add a new item to the soundboard
   */
  public async addItem(item: SoundboardItem): Promise<SoundboardStorage> {
    const current = this.loadSoundboard();
    
    // Check storage limits
    const totalSize = current.items.reduce((sum, i) => sum + i.fileSize, 0);
    if (totalSize + item.fileSize > MAX_STORAGE_SIZE) {
      throw new Error('Soundboard storage limit exceeded. Please remove some items.');
    }

    if (current.items.length >= MAX_ITEMS) {
      throw new Error('Maximum number of soundboard items reached. Please remove some items.');
    }

    // Add item with next order number
    const newItem = {
      ...item,
      order: current.items.length
    };

    const updatedStorage: SoundboardStorage = {
      ...current,
      items: [...current.items, newItem],
      lastUpdated: new Date()
    };

    await this.saveSoundboard(updatedStorage);
    return updatedStorage;
  }

  /**
   * Remove an item from the soundboard
   */
  public async removeItem(itemId: string): Promise<SoundboardStorage> {
    const current = this.loadSoundboard();
    
    // Clean up blob URL
    const itemToRemove = current.items.find(item => item.id === itemId);
    if (itemToRemove && itemToRemove.audioUrl.startsWith('blob:')) {
      URL.revokeObjectURL(itemToRemove.audioUrl);
    }

    const updatedStorage: SoundboardStorage = {
      ...current,
      items: current.items.filter(item => item.id !== itemId),
      lastUpdated: new Date()
    };

    await this.saveSoundboard(updatedStorage);
    return updatedStorage;
  }

  /**
   * Reorder items in the soundboard
   */
  public async reorderItems(items: SoundboardItem[]): Promise<SoundboardStorage> {
    const current = this.loadSoundboard();
    
    const updatedItems = items.map((item, index) => ({
      ...item,
      order: index
    }));

    const updatedStorage: SoundboardStorage = {
      ...current,
      items: updatedItems,
      lastUpdated: new Date()
    };

    await this.saveSoundboard(updatedStorage);
    return updatedStorage;
  }

  /**
   * Update expanded state
   */
  public async updateExpandedState(isExpanded: boolean): Promise<SoundboardStorage> {
    const current = this.loadSoundboard();
    
    const updatedStorage: SoundboardStorage = {
      ...current,
      isExpanded,
      lastUpdated: new Date()
    };

    await this.saveSoundboard(updatedStorage);
    return updatedStorage;
  }

  /**
   * Clear all soundboard data
   */
  public clearSoundboard(): void {
    const current = this.loadSoundboard();
    
    // Clean up all blob URLs
    current.items.forEach(item => {
      if (item.audioUrl.startsWith('blob:')) {
        URL.revokeObjectURL(item.audioUrl);
      }
    });

    localStorage.removeItem(STORAGE_KEY);
  }

  /**
   * Get storage usage statistics
   */
  public getStorageStats(): { totalSize: number; itemCount: number; maxSize: number; maxItems: number } {
    const current = this.loadSoundboard();
    const totalSize = current.items.reduce((sum, item) => sum + item.fileSize, 0);
    
    return {
      totalSize,
      itemCount: current.items.length,
      maxSize: MAX_STORAGE_SIZE,
      maxItems: MAX_ITEMS
    };
  }

  /**
   * Convert File to base64 for storage
   */
  private fileToBase64(file: File): string {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        // Remove data URL prefix to save space
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    }) as any; // Type assertion for simplicity
  }

  /**
   * Convert base64 back to File
   */
  private fileFromBase64(base64: string, filename: string, mimeType: string): File {
    try {
      // Validate base64 string
      if (!base64 || typeof base64 !== 'string') {
        throw new Error('Invalid base64 data');
      }
      
      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      
      const byteArray = new Uint8Array(byteNumbers);
      return new File([byteArray], filename, { type: mimeType });
    } catch (error) {
      console.error('Error converting base64 to file:', error);
      // Return a dummy file to prevent crashes
      return new File([''], filename, { type: mimeType });
    }
  }

  /**
   * Get default storage structure
   */
  private getDefaultStorage(): SoundboardStorage {
    return {
      items: [],
      isExpanded: false,
      lastUpdated: new Date()
    };
  }
}

export const soundboardStorage = SoundboardStorageManager.getInstance();
